import { LieferantDetailsDialogComponent } from '../../lieferant/lieferant-details-dialog/lieferant-details-dialog.component';
import { StammdatenService } from '../../stammdaten.service';
import { API_BASE_URL_SERVICE } from '../../../../kiene-core.config';
import { AppService } from '../../../../services/app/app.service';
import { KieneBackendApiService } from '../../../../services/backend-api/kiene-backend-api.service';
import { MessageService } from '../../../../services/message-service/message.service';
import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { Futtermittellieferant } from '../lieferant';
import { LocalStorageService } from '../../../../services/local-storage/local-storage.service';
import { Client } from '../../../client/classes/client';

@Component({
    selector: 'kiene-lieferant-uebersicht',
    templateUrl: './lieferant-uebersicht.component.html',
    styleUrls: ['./lieferant-uebersicht.component.scss'],
})
export class LieferantUebersichtComponent implements OnInit, OnDestroy {
    @Input('showReturnButton') showReturnButton = true;
    favoritenLieferant: Futtermittellieferant[] = [];
    sortField: 'bezeichnung' | 'ort' | 'qs_id' = 'bezeichnung';
    sortDirection: 'asc' | 'desc' = 'asc';
    lieferantFehlt = this.stammdatenService.getLieferantFehlt();
    watchLieferantFehltSub: Subscription;
    lieferantFavoritenSubscription: Subscription;
    kundenSubscription: Subscription;
    kunden: Client[] = [];

    constructor(
        private stammdatenService: StammdatenService,
        private api: KieneBackendApiService,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
        private messageService: MessageService,
        private localStorageService: LocalStorageService,
        private appService: AppService,
        private dialog: MatDialog,
    ) {}

    ngOnInit(): void {
        this.ladeKunden();
        this.ladeFavoriten();

        this.watchLieferantFehltSub = this.stammdatenService.watchEntsorgerFehlt().subscribe(fehlt => {
            this.lieferantFehlt = fehlt;
        });
    }

    ngOnDestroy(): void {
        this.watchLieferantFehltSub?.unsubscribe();
        this.lieferantFavoritenSubscription?.unsubscribe();
        this.kundenSubscription?.unsubscribe();
    }

    ladeFavoriten(): void {
        this.lieferantFavoritenSubscription?.unsubscribe();
        this.lieferantFavoritenSubscription = this.api.getAll(`${this.apiBaseUrl}futtermittellieferant/read_all.php`).subscribe({
            next: data => {
                const benutzerId = Number(this.localStorageService.getCurrentUser()?.benutzer_id);
                const records: Futtermittellieferant[] = data.records ?? [];
                const filtered = Number.isFinite(benutzerId)
                    ? records.filter(l => Number(l?.erstellt_von) === benutzerId)
                    : [];
                this.favoritenLieferant = this.mapVerbundeneKunden(filtered);
            },
            error: (err: any) => {
                this.messageService.errorMessage('Fehler beim Laden aller Lieferanten', err);
            },
        });
    }

    private ladeKunden(): void {
        this.kundenSubscription?.unsubscribe();
        this.kundenSubscription = this.stammdatenService.ladeVvvos().subscribe({
            next: response => {
                this.kunden = response?.records ?? [];
                this.favoritenLieferant = this.mapVerbundeneKunden(this.favoritenLieferant);
            },
            error: err => {
                this.messageService.errorMessage('Fehler beim Laden der Betriebe', err);
            },
        });
    }

    private mapVerbundeneKunden(lieferanten: Futtermittellieferant[]): Futtermittellieferant[] {
        const kundenByLieferant = new Map<number, string[]>();
        for (const kunde of this.kunden ?? []) {
            const lieferantId = Number(kunde?.futtermittellieferant_id);
            if (!Number.isFinite(lieferantId) || lieferantId <= 0) {
                continue;
            }
            const kundeLabel = kunde?.bezeichnung_final || kunde?.vvvo || `Kunde ${kunde?.kunde_id}`;
            if (!kundenByLieferant.has(lieferantId)) {
                kundenByLieferant.set(lieferantId, []);
            }
            kundenByLieferant.get(lieferantId).push(kundeLabel);
        }

        return (lieferanten ?? []).map(lieferant => {
            const verbundeneKunden = kundenByLieferant.get(Number(lieferant?.futtermittellieferant_id)) ?? [];
            return {
                ...lieferant,
                verbundene_kunden: verbundeneKunden.length ? verbundeneKunden.join(', ') : '-',
            };
        });
    }

    setSort(field: 'bezeichnung' | 'ort' | 'qs_id'): void {
        if (this.sortField === field) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
            return;
        }

        this.sortField = field;
        this.sortDirection = 'asc';
    }

    getSortedLieferanten(): Futtermittellieferant[] {
        const list = this.favoritenLieferant ?? [];
        const direction = this.sortDirection === 'asc' ? 1 : -1;

        return [...list].sort((a: Futtermittellieferant, b: Futtermittellieferant) => {
            const aValue = (a?.[this.sortField] ?? '').toString().toLowerCase();
            const bValue = (b?.[this.sortField] ?? '').toString().toLowerCase();
            return aValue.localeCompare(bValue, 'de') * direction;
        });
    }

    futtermittellieferantHinzufuegen(): void {
        const dialogRef = this.dialog.open(LieferantDetailsDialogComponent, {
            minWidth: this.appService?.isMobileScreen ? 'auto' : '460px',
            width: this.appService?.isMobileScreen ? 'calc(100vw - 24px)' : '',
            maxWidth: this.appService?.isMobileScreen ? 'calc(100vw - 24px)' : '',
        });

        dialogRef.afterClosed().subscribe({
            next: (result: Futtermittellieferant) => {
                if (!result) {
                    return;
                }
                this.ladeFavoriten();
            },
        });
    }

    bearbeiteLieferant(futtermittellieferant: Futtermittellieferant): void {
        const dialogRef = this.dialog.open(LieferantDetailsDialogComponent, {
            minWidth: this.appService?.isMobileScreen ? '100vw' : '460px',
            width: this.appService?.isMobileScreen ? '100%' : '',
            data: { futtermittellieferant },
        });

        dialogRef.afterClosed().subscribe({
            next: (result: Futtermittellieferant) => {
                if (result) {
                    this.ladeFavoriten();
                }
            },
        });
    }

    loescheLieferant(futtermittellieferant: Futtermittellieferant): void {
        const id = Number(futtermittellieferant?.futtermittellieferant_id);
        if (!Number.isFinite(id) || id <= 0) {
            return;
        }
        this.stammdatenService.deleteFuttermittellieferant(id).subscribe({
            next: () => {
                this.ladeFavoriten();
            },
            error: err => {
                this.messageService.errorMessage('Fehler beim Löschen des Futtermittellieferanten.', err);
            },
        });
    }

    get isMobileScreen(): boolean {
        return this.appService.isMobileScreen;
    }

    goBack(): void {
        window.history.back();
    }
}
